/* ============================================================
   Calming Care PDP — shared interactions & animations
   Idempotent: safe to load once even if linked by several sections.
   Drives: hero rain/star weather, scroll reveals, the "calm the brain"
   sticky-scroll card highlight, FAQ tabs + accordion, plan selector,
   size pills, and sticky add-to-cart visibility. Pure progressive
   enhancement — the page is fully functional without it.
   ============================================================ */
(function () {
  if (window.__ccInit) return;
  window.__ccInit = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- hero weather: build raindrops + stars ---------- */
  function buildWeather(root) {
    if (reduce) return;
    var rain = root.querySelector('[data-cc-rain]');
    var stars = root.querySelector('[data-cc-stars]');
    if (rain && !rain.children.length) {
      var frag = document.createDocumentFragment();
      for (var i = 0; i < 46; i++) {
        var d = document.createElement('div');
        d.className = 'cc-raindrop';
        var left = (i * 2.17 + (i % 5) * 1.3) % 100;
        d.style.left = left.toFixed(2) + '%';
        d.style.top = (-(i % 7) * 40 - 10) + 'px';
        d.style.animationDuration = (0.7 + (i % 6) * 0.14).toFixed(2) + 's';
        d.style.animationDelay = ((i % 11) * 0.13).toFixed(2) + 's';
        d.style.opacity = (0.35 + (i % 4) * 0.12).toFixed(2);
        frag.appendChild(d);
      }
      rain.appendChild(frag);
    }
    if (stars && !stars.children.length) {
      var f2 = document.createDocumentFragment();
      for (var j = 0; j < 40; j++) {
        var s = document.createElement('div');
        s.className = 'cc-star';
        s.style.left = ((j * 2.6 + (j % 6) * 1.7) % 100).toFixed(2) + '%';
        s.style.top = ((j * 2.3 + (j % 9) * 3) % 92).toFixed(2) + '%';
        var sc = (0.5 + (j % 5) * 0.2).toFixed(2);
        s.style.width = s.style.height = (2 + (j % 3)) + 'px';
        s.style.transform = 'scale(' + sc + ')';
        s.style.animationDuration = (2 + (j % 5) * 0.6).toFixed(2) + 's';
        s.style.animationDelay = ((j % 13) * 0.21).toFixed(2) + 's';
        f2.appendChild(s);
      }
      stars.appendChild(f2);
    }
  }

  /* ---------- scroll reveal ---------- */
  function initReveal(root) {
    var els = root.querySelectorAll('.cc-reveal');
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('cc-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('cc-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- "calm the brain" scroll-driven card highlight ----------
     Behaviour: the active card sits centred in the scroller and highlighted;
     scrolling moves the previous card up + greys it out, and whichever card's
     centre is nearest the scroller's centre becomes active. Cards 0..n map to
     the heart labels ("ANXIOUS|SETTLING|CALM"). Vertical padding is computed
     so the FIRST card starts centred and the LAST card can reach centre. */
  function initBrainScroll(root) {
    var scroller = root.querySelector('[data-cc-cba-scroll]');
    if (!scroller || scroller.dataset.ccCbaInit) return;
    scroller.dataset.ccCbaInit = '1';
    var cards = Array.prototype.slice.call(scroller.querySelectorAll('[data-cc-cba]'));
    if (!cards.length) return;
    var heart = root.querySelector('[data-cc-heart]');
    var labels = (root.getAttribute('data-cc-heart-labels') || 'ANXIOUS|SETTLING|CALM').split('|');

    function pad() {
      var h = scroller.clientHeight;
      var first = cards[0].offsetHeight;
      var last = cards[cards.length - 1].offsetHeight;
      scroller.style.paddingTop = Math.max(0, (h - first) / 2) + 'px';
      scroller.style.paddingBottom = Math.max(0, (h - last) / 2) + 'px';
    }
    function update() {
      var mid = scroller.scrollTop + scroller.clientHeight / 2;
      var best = 0, bestD = Infinity;
      cards.forEach(function (c, i) {
        var d = Math.abs(c.offsetTop + c.offsetHeight / 2 - mid);
        if (d < bestD) { bestD = d; best = i; }
      });
      cards.forEach(function (c, i) { c.classList.toggle('cc-cba--on', i === best); });
      if (heart && labels[best]) heart.textContent = labels[best];
    }
    pad();
    scroller.scrollTop = 0; /* card 0 centred + highlighted on load */
    update();
    /* direct call, not rAF-wrapped: update is O(cards) cheap, and rAF pauses in
       hidden/prerendered tabs which would freeze the highlight */
    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', function () { pad(); update(); });
    /* re-measure once images/fonts settle */
    window.addEventListener('load', function () { pad(); update(); });
  }

  /* ---------- FAQ tabs + accordion ---------- */
  function initFaq(root) {
    root.querySelectorAll('[data-cc-faq]').forEach(function (faq) {
      faq.querySelectorAll('[data-cc-faq-q]').forEach(function (q) {
        q.addEventListener('click', function () {
          var item = q.closest('[data-cc-faq-item]');
          var open = item.classList.toggle('cc-faq--open');
          var sign = q.querySelector('[data-cc-faq-sign]');
          if (sign) sign.textContent = open ? '−' : '+';
        });
      });
      var tabs = faq.querySelectorAll('[data-cc-faq-tab]');
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var group = tab.getAttribute('data-cc-faq-tab');
          tabs.forEach(function (t) { t.classList.toggle('cc-tab--on', t === tab); });
          faq.querySelectorAll('[data-cc-faq-item]').forEach(function (it) {
            var g = it.getAttribute('data-cc-group');
            it.style.display = (!g || g === group) ? '' : 'none';
          });
        });
      });
    });
  }

  /* ---------- buy box ----------
     Variant pills, price and CTA state are handled by cc-product.liquid's own
     inline script (self-contained variant engine). The plan selector was
     removed — purchase options render via the Seal Subscriptions widget. */

  /* ---------- sticky ATC bar visibility (show after hero buy box scrolls past) ---------- */
  function initStickyAtc(root) {
    var bar = root.querySelector('[data-cc-sticky]');
    var anchor = root.querySelector('[data-cc-buybox]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { bar.classList.toggle('cc-sticky--show', !e.isIntersecting && e.boundingClientRect.top < 0); });
    }, { threshold: 0 });
    io.observe(anchor);
  }

  /* ---------- Seal purchase options: savings maths + perks lines ----------
     Seal only shows a Save % for percentage plans. Multi-box plans are set up at a fixed
     price (e.g. 2 boxes every 55 days for $69.99), so Seal shows no saving and the bigger
     number reads as a worse deal. From the real selling-plan prices cc-product embeds
     ([data-cc-plan-data]) this paints, for the plan picked in each subscription option:
     "Save x%", the struck one-time equivalent, and "2 boxes every 55 days · $35.00/box ·
     free shipping · cancel anytime"; plus a note under the one-time option. Seal owns and
     re-renders this DOM, so a MutationObserver repaints (idempotently) after it changes. */
  function initPlanMath(root) {
    var dataEl = root.querySelector('[data-cc-plan-data]');
    var widget = root.querySelector('.sealsubs-target-element');
    if (!dataEl || !widget || widget.dataset.ccPlanInit) return;
    widget.dataset.ccPlanInit = '1';
    var form = widget.closest('form');
    var idInput = form && form.querySelector('input[name="id"]');
    var OBS = { childList: true, subtree: true, characterData: true };
    var timer = null;
    var obs = new MutationObserver(schedule);

    function intervalDays(plan) {
      var s = (plan.name || '') + ' ' + (plan.options || []).join(' ');
      var m = s.match(/(\d+(?:\.\d+)?)\s*(day|week|month|year)/i);
      if (m) {
        var unit = m[2].toLowerCase();
        return parseFloat(m[1]) * (unit === 'week' ? 7 : unit === 'month' ? 30 : unit === 'year' ? 365 : 1);
      }
      if (/weekly/i.test(s)) return 7;
      if (/monthly/i.test(s)) return 30;
      return 0;
    }

    /* format like Seal's own prices (e.g. "US$44.99") so all the numbers read as one set */
    function money(cents) {
      var sample = widget.querySelector('.sls-price .money') || widget.querySelector('.sls-price');
      var m = sample && sample.textContent.trim().match(/^(.*?)\d[\d.,]*(.*)$/);
      var amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return (m ? m[1] : '$') + amount + (m ? m[2] : '');
    }

    /* keep a single .cls child in parent holding text (placed after `after`, or first);
       remove it when text is empty */
    function put(parent, cls, tag, text, after) {
      if (!parent) return;
      var el = parent.querySelector(':scope > .' + cls);
      if (!text) { if (el) el.remove(); return; }
      if (!el) {
        el = document.createElement(tag);
        el.className = cls;
        if (after === 'first') parent.insertBefore(el, parent.firstChild);
        else if (after && after.parentNode === parent) parent.insertBefore(el, after.nextSibling);
        else parent.appendChild(el);
      }
      if (el.textContent !== text) el.textContent = text;
    }

    function paint() {
      timer = null;
      obs.disconnect();
      try {
        var data = JSON.parse(dataEl.textContent);
        var cfg = data.config || {};
        var v = idInput && data.variants[idInput.value];
        if (!v) return;
        var perBox = cfg.boxDays || 30;
        var boxesFor = function (p) { var d = intervalDays(p); return d ? Math.max(1, Math.round(d / perBox)) : 1; };
        widget.classList.toggle('cc-plan-math', !!cfg.math);

        var oneRadio = widget.querySelector('[data-one-time-option-radio]');
        var oneBox = oneRadio && oneRadio.closest('.sls-option-container');
        var oneLabel = oneBox && oneBox.querySelector('.sls-label-container');
        if (oneLabel) put(oneLabel, 'cc-sub-note', 'div', cfg.oneTimeNote || '', oneLabel.querySelector('.sls-selling-plan-group-first-line'));

        widget.querySelectorAll('.sls-option-container[data-selling-plan-group]').forEach(function (box) {
          var sel = box.querySelector('select[name="subs_interval"]');
          var plan = sel && v.plans[sel.value];
          var labelBox = box.querySelector('.sls-label-container');
          var save = '', was = '', lead = '';
          if (plan && cfg.math) {
            var days = intervalDays(plan), boxes = boxesFor(plan), base = v.price * boxes;
            if (cfg.base === 'subscription' && boxes > 1) {
              /* compare with the cheapest 1-box plan instead of the one-time price */
              var single = null;
              Object.keys(v.plans).forEach(function (id) {
                var p = v.plans[id];
                if (boxesFor(p) === 1 && (!single || p.price < single.price)) single = p;
              });
              if (single) base = single.price * boxes;
            }
            var pct = base > 0 ? Math.round((1 - plan.price / base) * 100) : 0;
            if (pct >= 1) { save = 'Save ' + pct + '%'; was = money(base); }
            lead = boxes > 1
              ? boxes + ' boxes every ' + days + ' days · ' + money(Math.round(plan.price / boxes)) + '/box'
              : (days ? 'Delivered every ' + days + ' days' : '');
          }
          put(box.querySelector('.sls-selling-plan-group-name'), 'cc-sub-save', 'span', save);
          put(box.querySelector('.sls-selling-plan-group-price'), 'cc-sub-was', 'div', was, 'first');
          put(labelBox, 'cc-sub-note', 'div', [lead, cfg.subNote].filter(Boolean).join(' · '),
            labelBox && labelBox.querySelector('.sls-selling-plan-group-first-line'));
        });
      } catch (e) {
        /* unreadable data: leave Seal's own rendering as it is */
      } finally {
        obs.observe(widget, OBS);
      }
    }
    function schedule() { if (!timer) timer = setTimeout(paint, 30); }

    obs.observe(widget, OBS);
    if (form) form.addEventListener('change', schedule);
    paint();
  }

  /* ---------- lower "Add to cart" buttons → smooth-scroll up to the buy box ---------- */
  function initToBuybox(root) {
    root.querySelectorAll('[data-cc-to-buybox]').forEach(function (a) {
      if (a.dataset.ccToBuyboxInit) return;
      a.dataset.ccToBuyboxInit = '1';
      a.addEventListener('click', function (e) {
        var box = document.querySelector('[data-cc-buybox]');
        if (!box) {
          /* no buy box on this page: use the merchant's link if there is one */
          var fallback = a.getAttribute('data-cc-fallback');
          if (fallback) { e.preventDefault(); window.location.href = fallback; }
          return;
        }
        e.preventDefault();
        box.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        box.focus({ preventScroll: true });
        /* content above can grow while the smooth scroll runs (lazy images), leaving the
           buy box tucked under the sticky header — settle exactly once the scroll ends */
        var settle = function () {
          window.removeEventListener('scrollend', settle);
          var drift = box.getBoundingClientRect().top - (parseFloat(getComputedStyle(box).scrollMarginTop) || 0);
          if (Math.abs(drift) > 4) box.scrollIntoView({ block: 'start' });
        };
        if ('onscrollend' in window) window.addEventListener('scrollend', settle);
        else setTimeout(settle, 1000);
      });
    });
  }

  function initAll() {
    document.querySelectorAll('.cc').forEach(function (root) {
      buildWeather(root);
      initReveal(root);
      initBrainScroll(root);
      initFaq(root);
      initStickyAtc(root);
      initToBuybox(root);
      initPlanMath(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  // re-init when sections are re-rendered in the theme editor
  document.addEventListener('shopify:section:load', initAll);
})();
